import { Menu as BaseUIMenu } from "@base-ui/react/menu";
import Card, { CardVariant, CardContent } from "./card";
import { cn } from "@/lib/utils";
import Link from "./page-link";

interface MenuProps {
	children: React.ReactNode;
	trigger: React.ReactNode;
	variant?: CardVariant;
	/** Accessible name for the trigger. Required when the trigger has no visible text. */
	triggerLabel?: string;
}

const Menu: React.FC<MenuProps> = ({ children, trigger, variant = "menu", triggerLabel }) => {
	return (
		<BaseUIMenu.Root>
			<BaseUIMenu.Trigger aria-label={triggerLabel} title={triggerLabel}>
				{trigger}
			</BaseUIMenu.Trigger>

			<BaseUIMenu.Portal className="z-50000">
				<BaseUIMenu.Positioner className="outline-hidden" sideOffset={8}>
					<BaseUIMenu.Popup className="origin-(--transform-origin) transition-[transform,scale,opacity] data-ending-style:scale-90 data-ending-style:opacity-0 data-starting-style:scale-90 data-starting-style:opacity-0">
						<Card variant={variant}>
							<CardContent className="gap-0">{children}</CardContent>
						</Card>
					</BaseUIMenu.Popup>
				</BaseUIMenu.Positioner>
			</BaseUIMenu.Portal>
		</BaseUIMenu.Root>
	);
};

interface MenuTriggerProps {
	children: React.ReactNode;
}

export const MenuTrigger: React.FC<MenuTriggerProps> = ({ children }) => {
	return <BaseUIMenu.Trigger>{children}</BaseUIMenu.Trigger>;
};

interface MenuItemProps {
	children: React.ReactNode;
	className?: string;
	href?: string;
	target?: React.HTMLAttributeAnchorTarget;
	rel?: string;
	title?: string;
}

export const MenuItem: React.FC<MenuItemProps> = ({ children, className, href, target, rel, title }) => {
	return (
		<BaseUIMenu.Item
			title={title}
			render={href ? <Link href={href} target={target} rel={rel} /> : undefined}
			className={cn(
				"hover:bg-[color-mix(in_srgb,var(--ms-card-background-color)_95%,black)] cursor-pointer rounded-(--ms-card-border-bottom-left-radius) px-[calc(var(--ms-card-padding-left)/1.2)] py-[calc(var(--ms-card-padding-top)/1.2)]",
				className,
			)}
		>
			{children}
		</BaseUIMenu.Item>
	);
};

export default Menu;
